/**
 * Thumbnail orchestration.
 *
 * - Keeps a singleton Worker that shrinks original photos to JPEG thumbs.
 * - Persists generated thumbs to IndexedDB (`component-image-thumbs`).
 * - Falls back to a main-thread `<canvas>` when `OffscreenCanvas`/Worker paths
 *   aren't available (older Safari).
 */

import {
  loadComponentThumbs,
  saveComponentThumbs,
} from "@/lib/storage/indexeddb";

const MAX_EDGE = 1024;
const QUALITY = 0.9;

/** Bumped whenever MAX_EDGE/QUALITY change so cached thumbs from older versions
 *  are treated as absent on read and regenerated at the new resolution. */
export const THUMB_VERSION = 2;

type WorkerResponse =
  | { id: string; ok: true; thumbBytes: Uint8Array }
  | { id: string; ok: false; error: string };

let workerInstance: Worker | null = null;
let workerAvailable: boolean | null = null;
const pending = new Map<string, (res: WorkerResponse) => void>();
let reqCounter = 0;

function getWorker(): Worker | null {
  if (workerAvailable === false) return null;
  if (workerInstance) return workerInstance;
  try {
    if (
      typeof Worker === "undefined" ||
      typeof OffscreenCanvas === "undefined" ||
      typeof createImageBitmap === "undefined"
    ) {
      workerAvailable = false;
      return null;
    }
    workerInstance = new Worker(
      new URL("./thumbnail-worker.ts", import.meta.url),
      { type: "module" },
    );
    workerInstance.addEventListener("message", (evt: MessageEvent<WorkerResponse>) => {
      const resolve = pending.get(evt.data.id);
      if (resolve) {
        pending.delete(evt.data.id);
        resolve(evt.data);
      }
    });
    workerInstance.addEventListener("error", (evt) => {
      console.warn("[thumbnails] worker error, falling back to main thread:", evt.message);
      workerAvailable = false;
      workerInstance?.terminate();
      workerInstance = null;
    });
    workerAvailable = true;
    return workerInstance;
  } catch (err) {
    console.warn("[thumbnails] worker init failed, using main-thread fallback:", err);
    workerAvailable = false;
    return null;
  }
}

function mimeFor(filename: string): string {
  const ext = filename.toLowerCase().split(".").pop();
  return ext === "png" ? "image/png" : "image/jpeg";
}

async function generateViaWorker(bytes: Uint8Array, mime: string): Promise<Uint8Array | null> {
  const worker = getWorker();
  if (!worker) return null;
  const id = `t${++reqCounter}`;
  return new Promise<Uint8Array | null>((resolve) => {
    pending.set(id, (res) => {
      resolve(res.ok ? res.thumbBytes : null);
    });
    worker.postMessage({ id, bytes, mime, maxEdge: MAX_EDGE, quality: QUALITY });
  });
}

async function generateOnMainThread(bytes: Uint8Array, mime: string): Promise<Uint8Array | null> {
  try {
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = url;
      });
      const longest = Math.max(img.naturalWidth, img.naturalHeight);
      const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
      const dstW = Math.max(1, Math.round(img.naturalWidth * scale));
      const dstH = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, dstW, dstH);
      const blob: Blob | null = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", QUALITY);
      });
      if (!blob) return null;
      const buf = await blob.arrayBuffer();
      return new Uint8Array(buf);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.warn("[thumbnails] main-thread generation failed:", err);
    return null;
  }
}

/**
 * Get thumbnail bytes for an image. Returns cached bytes if present,
 * otherwise generates from the provided original bytes, persists, and returns.
 */
export async function getOrGenerateThumb(
  projectId: string,
  storageKey: string,
  filename: string,
  originalBytes: Uint8Array,
): Promise<Uint8Array | null> {
  const mime = mimeFor(filename);
  let thumb = await generateViaWorker(originalBytes, mime);
  if (!thumb) thumb = await generateOnMainThread(originalBytes, mime);
  if (!thumb) return null;

  try {
    const single = new Map<string, Uint8Array>([[filename, thumb]]);
    await saveComponentThumbs(projectId, storageKey, single);
  } catch (err) {
    console.warn(`[thumbnails] persist failed for ${filename}:`, err);
  }
  return thumb;
}

/** Load thumbs already persisted for (project, storageKey). */
export async function loadThumbsForGroup(
  projectId: string,
  storageKey: string,
): Promise<Map<string, Uint8Array>> {
  try {
    return await loadComponentThumbs(projectId, storageKey);
  } catch (err) {
    console.warn(`[thumbnails] load failed for ${storageKey}:`, err);
    return new Map();
  }
}
